import { type MutableRefObject, useCallback, useEffect, useRef } from "react";
import { redPointApi } from "@/betby/api/services";
import { checkAndRefreshToken } from "@/betby/api/http";
import { useNavStore } from "@/betby/store/navStore";
import { sendGetWalletMessage } from "@/betby/models/host/unityMessageService";
import LogUtil from "@/betby/utils/LogUtil";

const RED_POINT_POLL_INTERVAL_MS = 10000;

interface UseBetbyRedPointPollingParams {
  currentMode: string;
  isInitializedRef: MutableRefObject<boolean>;
  refreshBalance: () => Promise<void>;
  setOrderRedPointCount: (count: number) => void;
  setNewOrderIds: (ids: string[]) => void;
}

/**
 * 管理 Betby 红点轮询：
 * - 周期拉取红点数据
 * - 货币切换时避免旧请求覆盖新模式结果
 * - 红点变化时联动余额刷新与钱包同步消息
 */
export function useBetbyRedPointPolling({
  currentMode,
  isInitializedRef,
  refreshBalance,
  setOrderRedPointCount,
  setNewOrderIds,
}: UseBetbyRedPointPollingParams) {
  const redPointPollingIntervalRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null);

  // 仅在 SC 模式做兜底清零，避免 GC 数据缺失时误清。
  const resetScRedPointIfNeeded = useCallback(
    (modeNow: string) => {
      if (modeNow !== "sc") return;
      const prevCount = useNavStore.getState().orderRedPointCount;
      if (prevCount !== 0) {
        setOrderRedPointCount(0);
        setNewOrderIds([]);
      }
    },
    [setOrderRedPointCount, setNewOrderIds],
  );

  const fetchRedPoints = useCallback(async () => {
    // 记录请求发起时的模式；响应到达时若模式已变则直接丢弃。
    const modeAtRequest = useNavStore.getState().currentMode;
    try {
      const redPointRes = await redPointApi.getAll();

      // 若切换过货币模式，忽略本次响应，避免「旧请求用 GC 数量覆盖 SC」导致红点一闪
      const modeNow = useNavStore.getState().currentMode;
      if (modeNow !== modeAtRequest) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const serverTime = (redPointRes as any).timestamp;
      if (serverTime) {
        checkAndRefreshToken(serverTime);
      }

      if (redPointRes.code === 200 && redPointRes.data?.msg?.bet_order_update) {
        const betOrderUpdate = redPointRes.data.msg.bet_order_update;
        const records =
          modeNow === "sc"
            ? betOrderUpdate.order_data
            : betOrderUpdate.gc_order_data;
        const count = Array.isArray(records) ? records.length : 0;
        const prevCount = useNavStore.getState().orderRedPointCount;

        if (count !== prevCount) {
          setOrderRedPointCount(count);
          if (Array.isArray(records)) {
            setNewOrderIds(records);
          }

          void refreshBalance();
          sendGetWalletMessage();
        }
        return;
      }

      resetScRedPointIfNeeded(modeNow);
    } catch (error) {
      LogUtil.error("[BetbyRenderer] 轮询获取数据失败:", error);

      const modeNow = useNavStore.getState().currentMode;
      if (modeNow === modeAtRequest) {
        resetScRedPointIfNeeded(modeNow);
      }
    }
  }, [
    refreshBalance,
    resetScRedPointIfNeeded,
    setOrderRedPointCount,
    setNewOrderIds,
  ]);

  const startRedPointPolling = useCallback(() => {
    if (redPointPollingIntervalRef.current) {
      return;
    }

    void fetchRedPoints();

    redPointPollingIntervalRef.current = setInterval(() => {
      void fetchRedPoints();
    }, RED_POINT_POLL_INTERVAL_MS);
  }, [fetchRedPoints]);

  const stopRedPointPolling = useCallback(() => {
    if (!redPointPollingIntervalRef.current) return;

    clearInterval(redPointPollingIntervalRef.current);
    redPointPollingIntervalRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      stopRedPointPolling();
    };
  }, [stopRedPointPolling]);

  // 当货币模式改变时，若 SDK 已初始化，立即刷新一次红点与余额
  useEffect(() => {
    if (!isInitializedRef.current) return;
    void fetchRedPoints();
    void refreshBalance();
  }, [currentMode, fetchRedPoints, isInitializedRef, refreshBalance]);

  return {
    startRedPointPolling,
    stopRedPointPolling,
  };
}
